import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx.js'
import { Icon, type IconName } from '../icons/index.js'

// Surfaces -----------------------------------------------------------------------

export function Card({ className, children, as: As = 'div', ...rest }: HTMLAttributes<HTMLElement> & { as?: 'div' | 'section' | 'article' | 'aside' | 'li' }) {
  return (
    <As className={cx('rh-card rounded-xl', className)} {...rest}>
      {children}
    </As>
  )
}

/** Small uppercase label used above numbers and in table headers. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3', className)}>{children}</div>
}

export function Stat({ label, children, hint, className }: { label: ReactNode; children: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <div className={cx('min-w-0', className)}>
      <Eyebrow>{label}</Eyebrow>
      <div className="font-display num mt-1 truncate text-[18px] font-semibold leading-tight text-ink">{children}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-3">{hint}</div>}
    </div>
  )
}

/**
 * The tone lives in the 6px dot (or the icon); the pill itself is a monochrome
 * outline. The former pastel tones (mint, lilac, …) no longer fill anything:
 * they are kept only as muted dot hues for older call sites.
 */
export type PillTone = 'neutral' | 'brand' | 'good' | 'bad' | 'warn' | 'info' | 'mint' | 'lilac' | 'peach' | 'sky' | 'butter' | 'rose'
const toneDot: Record<PillTone, string> = {
  neutral: 'bg-ink-4',
  brand: 'bg-accent',
  good: 'bg-gain',
  bad: 'bg-loss',
  warn: 'bg-warn',
  info: 'bg-accent',
  mint: 'bg-mint-deep',
  lilac: 'bg-lilac-deep',
  peach: 'bg-peach-deep',
  sky: 'bg-sky-deep',
  butter: 'bg-butter-deep',
  rose: 'bg-rose-deep',
}
const toneIcon: Record<PillTone, string> = {
  neutral: 'text-ink-3',
  brand: 'text-accent',
  good: 'text-gain',
  bad: 'text-loss',
  warn: 'text-warn',
  info: 'text-accent',
  mint: 'text-mint-deep',
  lilac: 'text-lilac-deep',
  peach: 'text-peach-deep',
  sky: 'text-sky-deep',
  butter: 'text-butter-deep',
  rose: 'text-rose-deep',
}

export function Pill({ tone = 'neutral', icon, children, className, title }: { tone?: PillTone; icon?: IconName | undefined; children: ReactNode; className?: string | undefined; title?: string | undefined }) {
  return (
    <span
      title={title}
      data-tone={tone}
      className={cx('inline-flex h-5 items-center gap-1.5 whitespace-nowrap rounded-[6px] border border-line-strong px-1.5 text-[11px] font-medium uppercase leading-none tracking-[0.06em] text-ink-2', className)}
    >
      {icon ? <Icon name={icon} size={11} strokeWidth={2} className={toneIcon[tone]} /> : <span aria-hidden="true" className={cx('h-1.5 w-1.5 shrink-0 rounded-full', toneDot[tone])} />}
      {children}
    </span>
  )
}

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div aria-hidden="true" style={style} className={cx('animate-pulse rounded-md bg-sunken', className)} />
}
